/**
 * What a reopen confirmation shows: the decision about to be cleared, and what
 * reopening does and does not do.
 *
 * Reopening used to be one click — on findings the status badge itself was the
 * trigger — and it erases the decision from the record: its verdict, note,
 * version and proof. The activity trail keeps the receipt, but nothing on the
 * page said so, or that reopening an applied suggestion leaves the change in
 * place. Pure, so the wording is pinned by tests.
 *
 * @module components/regulation/policy/ReopenDialog.logic
 */
import type { PolicyFinding, Recommendation } from "../../../state/governance";

export interface ReopenFact {
  readonly label: string;
  readonly value: string;
}

export interface ReopenSummary {
  /** "finding" or "suggestion" — what is being reopened. */
  readonly what: "finding" | "suggestion";
  readonly facts: ReadonlyArray<ReopenFact>;
  /** When the decision was made; the dialog formats it like the rest of the page. */
  readonly decidedAt: string | null;
  /** What reopening does to the record, and what it leaves alone. */
  readonly consequence: string;
}

const present = (facts: ReadonlyArray<ReopenFact | null>): ReopenFact[] =>
  facts.filter((fact): fact is ReopenFact => fact !== null && fact.value.trim().length > 0);

const fact = (label: string, value: string | null | undefined): ReopenFact | null =>
  value == null ? null : { label, value };

/** A decided finding's record, as the reopen confirmation lists it. */
export function findingReopenSummary(
  finding: Pick<
    PolicyFinding,
    "status" | "resolvedVersion" | "resolutionNote" | "resolvedByEmail" | "resolvedAt" | "decisionProof"
  >,
): ReopenSummary {
  return {
    what: "finding",
    facts: present([
      fact("Decision", finding.status === "resolved" ? "Resolved" : "Closed"),
      finding.status === "resolved" && finding.resolvedVersion != null
        ? fact("Version", `v${finding.resolvedVersion}`)
        : null,
      fact("Note", finding.resolutionNote),
      fact("Proof", finding.decisionProof?.filename),
      fact("By", finding.resolvedByEmail),
    ]),
    decidedAt: finding.resolvedAt ?? null,
    consequence: "Reopening clears this decision from the finding. It stays in the document's activity.",
  };
}

/** A decided suggestion's record, as the reopen confirmation lists it. */
export function suggestionReopenSummary(
  suggestion: Pick<
    Recommendation,
    | "status"
    | "policyId"
    | "resolvedVersion"
    | "resolvedPolicyName"
    | "decisionNote"
    | "decidedByEmail"
    | "decidedAt"
    | "decisionProof"
  >,
): ReopenSummary {
  const applied = suggestion.status === "applied";
  return {
    what: "suggestion",
    facts: present([
      fact("Decision", applied ? "Applied" : suggestion.status === "dismissed" ? "Rejected" : null),
      applied && suggestion.resolvedVersion != null ? fact("Version", `v${suggestion.resolvedVersion}`) : null,
      applied ? fact("Document", suggestion.resolvedPolicyName?.replace(/\.pdf$/i, "")) : null,
      fact("Note", suggestion.decisionNote),
      fact("Proof", suggestion.decisionProof?.filename),
      fact("By", suggestion.decidedByEmail),
    ]),
    decidedAt: suggestion.decidedAt ?? null,
    // A suggestion with no document (a missing one, a company-profile change)
    // has no document activity for the receipt to stay in.
    consequence: [
      "Reopening clears this decision from the suggestion.",
      suggestion.policyId !== null ? "It stays in the document's activity." : null,
      applied ? "Reopening does not undo the change that was applied." : null,
    ]
      .filter((part): part is string => part !== null)
      .join(" "),
  };
}

/**
 * Whether a pending reopen no longer needs its confirmation: the item is gone,
 * or it is already open — a live refresh, a colleague. Left set, the dialog
 * would reappear by itself the next time the item is decided.
 */
export function reopenTargetSettled<T extends { readonly id: string }>(
  id: string | null,
  items: ReadonlyArray<T> | null | undefined,
  isOpen: (item: T) => boolean,
): boolean {
  if (id === null || items == null) return false;
  const item = items.find((candidate) => candidate.id === id);
  return item === undefined || isOpen(item);
}

/** The attribute that marks the action taking a reopened item's next decision. */
export const REOPEN_FOCUS_ATTRIBUTE = "data-reopen-focus";

/**
 * Move focus to a reopened item's next decision (Amend, Resolve, Approve) once
 * it is rendered and enabled. The Reopen button that had focus is gone with the
 * decision, so without this focus falls to the page. True once focused.
 */
export function focusReopenedAction(
  root: Pick<ParentNode, "querySelector"> | null,
  id: string,
): boolean {
  const target = root?.querySelector<HTMLElement>(
    `[${REOPEN_FOCUS_ATTRIBUTE}="${id.replace(/["\\]/g, "\\$&")}"]`,
  );
  if (!target || (target as HTMLButtonElement).disabled) return false;
  target.focus();
  return true;
}
